// tslint:disable:variable-name
import { Flooring } from '../flooring/Flooring'
import { Bidder } from '../enums'
import { ExperimentFlag } from '../enums/ExperimentFlag'
import { Device } from '../helpers'
import { domReady } from '../helpers/DOM'
import { Experiment } from '../helpers/Experiment'
import { getQueryParam } from '../helpers/getQueryParam'
import Logger from '../helpers/Logger'
import { spread } from '../helpers/spread'
import { DiscrepancyValues } from '../prebidFacade/BidderSettings'
import { IBidRequest, IRoundelBidRequest } from '../../typings/IPrebid'
import { deepcopy } from '../helpers/deepcopy'
import { MediaType } from '../enums/MediaType'

export enum StaticSlots {
  leaderboard_atf = 'LeaderboardAtf',
  leaderboard_btf = 'LeaderboardBtf',
  sidebar_atf = 'SidebarAtf',
  sidebar_btf = 'SidebarBtf',
  // recipe = 'Recipe',
  recipe_btf = 'Recipe',
  recipe_mobile = 'Recipe',
  comments = 'Comments',
  feed = 'Feed'
}

/**
 *
 * This class acts as an adapter for mapping the
 * current APIs interface to the interface I hope the
 * API will transition to in the future. For example, we have
 * an array of slot models. The API currently doesn't deliver that
 * but will hopefully do so in the future. Once it does, we will
 * be able to remove the transform here and use it straight
 * from the API. The rest of the codebase will not require any
 * changes because we built on top of this interface from the start.
 *
 * Anything marked private on this model is intended to be
 * deprecated in the future
 */

export default class WebModel {
  readonly optimize_desktop_pagespeed: boolean
  readonly customTargeting: object
  readonly S2S: boolean
  readonly cpmIndices?: { [adUnitId: string]: number }
  readonly country?: string

  // Bidders

  aol: boolean
  appnexus: boolean
  appnexusAst: boolean
  brealtime: boolean
  google: boolean
  gumgum: boolean
  indexExchange: boolean
  openx: boolean
  pubmatic: boolean
  rubicon: boolean
  triplelift: boolean
  uam: boolean
  yieldmo: boolean

  discrepancies: DiscrepancyValues | null
  readonly experiment: Experiment
  readonly slots: Array<ISlotModel>
  readonly bidders: Array<IBidderModel>
  readonly bidRequests: IBidRequests
  adhesion_light: boolean
  adhesion_mobile: boolean
  adhesion_tablet: boolean
  adhesion_desktop: boolean
  adunit: string
  amazon: boolean
  category: ICategories
  categories: Array<ICategories>
  ccpa: '1' | '0'
  ccpa_link_loc: string
  custom_ccpa_text: string
  content_cba_mobile_buffer: number | undefined
  content_cba_desktop_buffer: number | undefined
  content_cba_mobile_limit: number | undefined
  content_cba_mobile_percentage: number
  content_cba_desktop_limit: number | undefined
  content_cba_desktop_percentage: number
  chicory: boolean
  cm36: boolean // disable content mobile 300x600 disable
  cb_dn: boolean // disable content btf native ('fluid')
  comments_selector: string
  comments_position: string
  comscore: boolean
  comscore_vertical: string
  content_selector: string | undefined
  content_selector_mobile: string | undefined
  content_buffer_begin: number
  content_buffer_end: number
  content_desktop_spacing: number
  content_stop_selector: string
  content_mobile_spacing: number
  content_require_text: number
  content_mobile: boolean
  content_desktop: boolean
  custom_cmp_header: string
  custom_css: string
  custom_cmp_color: string
  customPurposes: Array<{ id: number; name: string; description: string }>
  vendorList: Array<object>
  customTitle: string
  deactivated: boolean
  disable_newrelic: boolean
  display_ads: boolean
  divbuster_exclusions: string
  enable_divbuster: boolean
  feed_selector: string
  feed_position: InsertPosition
  floor: { [type in MediaType]: number }
  footer_branding: boolean
  footer_selector?: string
  gdpr: '0' | '1'
  gdpr_compliance: number
  gumgum_id: string
  gumgum_in_image: boolean
  gumgum_skip_first_image: boolean
  iabOptouts: string[]
  id: number
  isPrebid: boolean
  keywordSrc: string
  killswitch: boolean
  leaderboard: boolean
  leaderboard_atf_selector: string
  leaderboard_atf_position: string
  leaderboard_atf_disable_billboard: boolean
  leaderboard_btf_selector: string
  leaderboard_btf_position: string
  mediavine_comscore: boolean
  newRelicBidRequests: boolean
  optouts: Array<IOptouts>
  optimize_mobile_pagespeed: boolean
  owned: boolean
  pageIdSrc: string
  privacy_policy_link: string
  quantcast: boolean
  recipe_float: boolean
  recipe_position: string
  recipe_selector: string
  recipe_mobile_position: string
  recipe_mobile_selector: string
  ro_id: number
  samplingRate?: number
  st_d: string
  st_m: string
  sidebar_atf_position: string
  sidebar_atf_selector: string
  sidebar_btf_disable_sticky: boolean
  sidebar_btf_selector: string
  sidebar_btf_stop_selector: string
  sidebar_btf_sticky_offset: number
  sidebar_minimum_width: number
  slug: string
  title: string
  triplelift_sensitive: boolean
  topLevelCategory: {
    id: number
    title: string
    slug: string
    parentId: number
    iabCode: string
  }
  urlFilters: Array<IUrlFilter>
  versionGroup: IVersionGroup
  videoDiscrepancies: DiscrepancyValues | null
  videoSettings: IVideoSettings
  adhesionVideo: IVideo
  featuredVideo: IVideo
  wrapperSrc: string
  launcherUrl: string
  yieldmo_placement: number
  zergnet: boolean
  zergnet_content_position: InsertPosition
  zergnet_content_selector: string
  zergnet_id: number
  zergnet_traffic_id: number
  videoFloor: number

  constructor(model: IWebViewModel) {
    model.indexExchange = model.index
    // this isn't togglable from dashboard (yet) so its hardcoded true
    model.S2S = true

    // localModelOverrides is mostly for Trellis who want to be able to submit
    // overrides to the model. Yes we want to overwrite model, because some Static
    // modules rely on that global for data.
    const localModel = window.$mediavine.web.localModel || {}
    const localModelOverrides = isObject(localModel) ? localModel : {}
    model = { ...model, ...localModelOverrides }
    window.$mediavine.web.model = model

    spread(model).onto(this)
    this.experiment = new Experiment(model.experiments || [])

    // Reshaping model to match a future interface from API
    this.slots = convertSiteModelToSlotModels(this)
    this.bidders = convertSiteModelToPartnersModel(model)
    this.customTargeting = getCustomTargeting()
    this.floor = {
      [MediaType.banner]: Flooring.getDisplayFloor({ country: model.country }),
      [MediaType.video]: Flooring.getVideoFloor({ country: model.country })
    }

    Object.keys(this.bidRequests).forEach((adunit) => {
      if (adunit.indexOf('outstream') === -1) {
        this.bidRequests[adunit].unshift({
          bidder: Bidder.S2S,
          params: {}
        })
      }
    })

    Logger.debug(this)
  }

  get disableContentBtfNative(): boolean {
    return this.cb_dn
  }
}

function validateAdUnitType(model: WebModel, adUnitId: string): boolean {
  const valid = {
    adhesion_desktop: model.adhesion_desktop,
    adhesion_mobile: model.adhesion_mobile,
    adhesion_tablet: model.adhesion_tablet,
    comments: !!model.comments_selector,
    feed: !!model.feed_selector,
    leaderboard_atf: model.leaderboard && !!model.leaderboard_atf_selector,
    leaderboard_btf: model.leaderboard && !!model.leaderboard_btf_selector,
    recipe_mobile:
      Device.isMobileOrTablet &&
      (!!model.recipe_mobile_selector || !!model.recipe_selector),
    recipe_btf: !!model.recipe_selector,
    sidebar_btf: !!model.sidebar_btf_selector,
    sidebar_atf: !!model.sidebar_atf_selector
  }

  return valid[adUnitId] || false
}

function convertSiteModelToSlotModels(model: WebModel): Array<ISlotModel> {
  const slots: Array<ISlotModel> = []

  Object.keys(StaticSlots).map((slotId) => {
    const type = StaticSlots[slotId]
    // Recipes are different :(
    const modelLoookup = slotId.indexOf('recipe_') !== -1 ? 'recipe' : slotId
    if (validateAdUnitType(model, slotId)) {
      try {
        slots.push({
          adUnitId: slotId,
          type: type as SlotClass,
          selector: model[`${modelLoookup}_selector`],
          insertPosition: model[`${modelLoookup}_position`],
          mobileSelector: model[`${modelLoookup}_mobile_selector`],
          mobileInsertPosition: model[`${modelLoookup}_mobile_position`]
        })
      } catch (e) {
        Logger.debug(`error creating slot ${type}`, e)
      }
    }
  })

  domReady(() => {
    const elements = document.querySelectorAll(
      '.mv_recipe_target, .mv-create-target-selector'
    )

    Array.from(elements).forEach((element) => {
      slots.push({
        adUnitId: 'dynamic',
        insertPosition: 'beforeend',
        selector: element as HTMLElement,
        lazy: true,
        type: 'Recipe'
      })
    })
  })

  slots.forEach((slot) => {
    // hardcoded for sidebar_btf but in the future,
    // dashboard slot models will be able to assign these to any slot
    if (slot.adUnitId === 'sidebar_btf') {
      slot.stickyOffset = model.sidebar_btf_sticky_offset
      slot.sticky = !model.sidebar_btf_disable_sticky
      slot.stickyStopSelector = model.sidebar_btf_stop_selector
    }

    slot.lazy = slot.adUnitId.indexOf('atf') === -1
  })

  return slots
}

function convertSiteModelToPartnersModel(
  model: IWebViewModel
): Array<IBidderModel> {
  const partners: Array<IBidderModel> = []
  Object.keys(Bidder).map((value) => {
    const displayName = Bidder[value]
    if (value === Bidder.roundel && model.index) {
      partners.push({
        name: displayName
      })
    }
    if (model[value]) {
      partners.push({
        name: displayName
      })
    }
  })

  return partners
}

function getCustomTargeting(): object {
  return window.$mediavine.web.customTargeting || {}
}

export function optimizingForMobilePagespeed(): boolean {
  if (
    !window.$mediavine ||
    !window.$mediavine.web ||
    !window.$mediavine.web.model ||
    window.$mediavine.web.disable_pagespeed ||
    getQueryParam('optimizeSpeed') === 'false'
  ) {
    return false
  }

  return (
    getQueryParam('optimizeSpeed') === 'true' ||
    (window.$mediavine.web.model.optimize_mobile_pagespeed &&
      Device.isMobileOrTablet)
  )
}

export function optimizingForDesktopPagespeed(): boolean {
  if (
    !window.$mediavine ||
    !window.$mediavine.web ||
    !window.$mediavine.web.model ||
    window.$mediavine.web.disable_pagespeed ||
    getQueryParam('optimizeSpeed') === 'false'
  ) {
    return false
  }

  return (
    getQueryParam('optimizeSpeed') === 'true' ||
    (window.$mediavine.web.model.optimize_desktop_pagespeed &&
      !Device.isMobileOrTablet)
  )
}

export interface IBidRequests {
  [adunit: string]: Array<IBidRequest>
}

export function scrubBidder(
  requests: IBidRequests,
  bidder: Bidder
): IBidRequests {
  const newRequests = deepcopy(requests)
  Object.keys(newRequests).forEach((slot) => {
    newRequests[slot] = newRequests[slot].filter((b) => b.bidder !== bidder)
  })

  return newRequests
}

function isObject(a: any): boolean {
  return !!a && a.constructor === Object
}
